/**
 * Coin selection + ZIP-317 fee math for shielded ZEC sends.
 *
 * Vendored from leokit-sdk/src/wallets/shielded/coin-select.ts (which
 * leodex-v2 uses) — same algorithm, no behavioural change. We vendor
 * rather than depend so ZcashWallet doesn't pull leokit-sdk's heavy
 * multi-chain dep tree just for ~150 lines of arithmetic.
 *
 * Strategy: smallest-first greedy. Picks the smallest notes that
 * cover (amount + fee), minimising leftover dust and keeping the note
 * count under the ZIP-317 grace threshold when possible.
 */

import type { SpendableNote } from "../balance";

export interface CoinSelectInput {
  notes: SpendableNote[];
  /** Target send amount (zats). Does NOT include fee. */
  amount_zats: number;
  /** Total number of outputs the caller will add (user outputs only —
   *  change is computed here and the memo-note is signalled separately
   *  via `has_memo_note`). */
  num_outputs: number;
  /** Of `num_outputs`, how many are transparent. ZIP-317 counts
   *  transparent outputs as separate logical actions on top of
   *  max(sapling_spends, sapling_outputs). */
  num_transparent_outputs?: number;
  /** True when the signer will append a 0-value Sapling memo-note
   *  (MAYA-style shielded-memo swaps). Adds one to sapling_outputs. */
  has_memo_note?: boolean;
}

export interface SelectedSpend {
  notes: SpendableNote[];
  total_in_zats: number;
  fee_zats: number;
  change_zats: number;
}

export type CoinSelectErrorCode =
  | "INSUFFICIENT_FUNDS"
  | "NO_NOTES"
  | "ZERO_AMOUNT";

export class CoinSelectError extends Error {
  public readonly code: CoinSelectErrorCode;
  constructor(code: CoinSelectErrorCode, message: string) {
    super(message);
    this.name = "CoinSelectError";
    this.code = code;
  }
}

/**
 * ZIP-317 conventional fee.
 *
 *   logical_actions = transparent_in + transparent_out
 *                   + max(sapling_spends, sapling_outputs)
 *                   + orchard_actions
 *   fee = marginal_fee * max(grace_actions, logical_actions)
 *
 * The Sapling builder pads bundles to ≥2 outputs whenever there's any
 * Sapling activity. We match that here so the fee we precompute equals
 * what the signer will charge.
 */
const ZIP317_MARGINAL_FEE_ZATS = 5_000;
const ZIP317_GRACE_ACTIONS = 2;
const SAPLING_MIN_OUTPUTS = 2;

export function zip317Fee(args: {
  num_spends: number;
  num_outputs: number;
  has_change: boolean;
  num_transparent_outputs?: number;
  has_memo_note?: boolean;
}): number {
  const t_out = args.num_transparent_outputs ?? 0;
  const requested_sapling_outputs =
    args.num_outputs -
    t_out +
    (args.has_change ? 1 : 0) +
    (args.has_memo_note ? 1 : 0);
  const sapling_outputs =
    args.num_spends > 0 || requested_sapling_outputs > 0
      ? Math.max(requested_sapling_outputs, SAPLING_MIN_OUTPUTS)
      : 0;
  const sapling_actions = Math.max(args.num_spends, sapling_outputs);
  const logical_actions = t_out + sapling_actions;
  return (
    ZIP317_MARGINAL_FEE_ZATS *
    Math.max(ZIP317_GRACE_ACTIONS, logical_actions)
  );
}

export function selectCoins(input: CoinSelectInput): SelectedSpend {
  if (input.amount_zats <= 0) {
    throw new CoinSelectError("ZERO_AMOUNT", "amount must be positive");
  }
  if (input.notes.length === 0) {
    throw new CoinSelectError("NO_NOTES", "no spendable notes available");
  }

  const candidates = [...input.notes].sort(
    (a, b) => a.value_zats - b.value_zats,
  );
  const selected: SpendableNote[] = [];
  let total = 0;

  for (const note of candidates) {
    selected.push(note);
    total += note.value_zats;

    // Tentative fee assuming change. Final fee, if exact-match,
    // shrinks by one logical action but the surplus just goes to fee —
    // no risk of insufficient funds.
    const fee = zip317Fee({
      num_spends: selected.length,
      num_outputs: input.num_outputs,
      has_change: true,
      num_transparent_outputs: input.num_transparent_outputs,
      has_memo_note: input.has_memo_note,
    });
    if (total >= input.amount_zats + fee) {
      const change = total - input.amount_zats - fee;
      return {
        notes: selected,
        total_in_zats: total,
        fee_zats: fee,
        change_zats: change,
      };
    }
  }

  const max_fee = zip317Fee({
    num_spends: selected.length,
    num_outputs: input.num_outputs,
    has_change: false,
    num_transparent_outputs: input.num_transparent_outputs,
    has_memo_note: input.has_memo_note,
  });
  throw new CoinSelectError(
    "INSUFFICIENT_FUNDS",
    `have ${total} zats across ${selected.length} note(s), need ${input.amount_zats} + ${max_fee} fee`,
  );
}
